import type React from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSelf, deleteLoginCookie, useGetRole } from '@vlabs/api/artemy/client';
import {
  ActivityIcon,
  TechServerIcon,
  DetectionIcon,
  LocationCrosshairIcon,
  ShieldOnIcon,
  UserCardIcon,
  HistoryIcon,
  ChartPieIcon,
  VideoOnIcon,
} from '@vlabs/icons';
import { NavbarItemWithPermissions } from '@vlabs/monokit/entities/navbar-item-with-permissions/NavbarItemWithPermissions';
import {
  LanguageButton, NavbarPanel, NavbarPanelFooter, NavbarPanelHeader,
  NavbarPanelSection, NavbarPanelViewer, NavbarTypeToggleButton, ThemeButton,
  ViewerAuthButton, NavbarGroup, defaultLanguageOptions,
} from '@vlabs/monokit/shared/ui/navbar-panel';
import { WhiteLabelBrand } from '@vlabs/monokit/shared/ui/white-label-brand';
import { clearPermissionsCache } from '@vlabs/permissions';
import { matchPath, useLocation } from 'react-router-dom';

import { routes } from '../routes';
import { useNavigationTranslation } from './i18n';

// Опции языков формируются из build-time флагов локалей (__LOCALE_*):
// берём из defaultLanguageOptions только те локали, что включены в сборку.
const languageOptions = [
  ...(__LOCALE_RU__ ? defaultLanguageOptions.filter((option) => option.value === 'ru') : []),
  ...(__LOCALE_EN__ ? defaultLanguageOptions.filter((option) => option.value === 'en') : []),
  ...(__LOCALE_PTBR__ ? defaultLanguageOptions.filter((option) => option.value === 'ptBr') : []),
  ...(__LOCALE_ESES__ ? defaultLanguageOptions.filter((option) => option.value === 'esEs') : []),
];

function PointNavbar(): React.JSX.Element {
  const { t } = useNavigationTranslation();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { data: account } = useQuery({
    queryKey: ['self'],
    queryFn: getSelf,
    staleTime: Infinity,
  });
  const isAuthenticated = !!account;
  const { data: roleData } = useGetRole(account?.role_id ?? '', { query: { enabled: !!account?.role_id } });

  return (
    <NavbarPanel
      footer={(
        <NavbarPanelFooter
          viewer={(
            <NavbarPanelViewer
              name={account?.full_name ?? account?.email}
              role={roleData?.name}
            />
          )}
        >
          <ThemeButton />
          <LanguageButton languageOptions={languageOptions} />
          <ViewerAuthButton
            isAuthenticated={isAuthenticated}
            onLogout={async () => {
              try {
                await deleteLoginCookie();
              } catch {
                // ignore
              }
              // Сбрасываем кэш прав и сами права — иначе они протекут следующему пользователю
              clearPermissionsCache('point3');
              queryClient.setQueryData(['permissions', 'point3'], null);
              queryClient.setQueryData(['self'], null);
              window.history.replaceState({}, '', '/');
            }}
          />
        </NavbarPanelFooter>
      )}
      header={(
        <NavbarPanelHeader
          logo={<WhiteLabelBrand />}
          toggleButton={<NavbarTypeToggleButton />}
        />
      )}
      sections={[
        <NavbarPanelSection
          key="events"
          name={t('events_and_video')}
        >
          <NavbarItemWithPermissions
            data-testid="workspace"
            exact
            icon={<ActivityIcon />}
            name={t('workspace')}
            page="operator_panel"
            to={routes.events.latest}
          />
          <NavbarItemWithPermissions
            data-testid="fs_event_archive"
            icon={<HistoryIcon />}
            name={t('fs_event_archive')}
            page="events_fs_archive"
            to={routes.events.archive}
          />
          <NavbarItemWithPermissions
            data-testid="search"
            icon={<LocationCrosshairIcon />}
            name={t('search')}
            page="search"
            to={routes.search}
          />
          <NavbarItemWithPermissions
            data-testid="statistic"
            icon={<ChartPieIcon />}
            name={t('fs_event_statistic')}
            page="statistics_fs"
            to={routes.events.statistic.all}
          />
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="preferences"
          name={t('settings_and_catalogs')}
        >
          <NavbarItemWithPermissions
            canMethod="oneof"
            data-testid="cards_and_lists"
            icon={<UserCardIcon />}
            name={t('cards_and_lists')}
            page={['cards', 'lists']}
            to={routes.cardsAndLists.index}
          />
          <NavbarItemWithPermissions
            data-testid="handlers"
            icon={<DetectionIcon />}
            name={t('handlers')}
            page="handlers"
            to={routes.handlers.all}
          />
          <NavbarItemWithPermissions
            data-testid="sources"
            icon={<VideoOnIcon />}
            name={t('sources')}
            page="streams_fs"
            to={routes.sources.list}
          />
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="administration"
          name={t('administration')}
        >
          <NavbarGroup
            active={
              !!matchPath(pathname, { path: routes.users.list })
              || !!matchPath(pathname, { path: routes.roles.list })
              || !!matchPath(pathname, { path: routes.departments.list })
              || !!matchPath(pathname, { path: routes.permissionTags.list })
            }
            icon={<ShieldOnIcon />}
            name={t('access_control')}
          >
            <NavbarItemWithPermissions
              data-testid="users"
              name={t('users')}
              page="users"
              to={routes.users.list}
            />
            <NavbarItemWithPermissions
              data-testid="roles"
              name={t('roles')}
              page="roles"
              to={routes.roles.list}
            />
            <NavbarItemWithPermissions
              data-testid="departments"
              name={t('departments')}
              page="departments"
              to={routes.departments.list}
            />
            <NavbarItemWithPermissions
              data-testid="permission_tags"
              name={t('permission_tags')}
              page="permission_tags"
              to={routes.permissionTags.list}
            />
          </NavbarGroup>
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="other"
          name={t('other')}
        >
          <NavbarItemWithPermissions
            data-testid="monitoring"
            icon={<TechServerIcon />}
            name={t('monitoring')}
            page="monitoring"
            to={routes.app.info}
          />
        </NavbarPanelSection>,
      ]}
    />
  );
}

export { PointNavbar };
